import {
  AdminLegendVariant,
  PharmacistLegendVariant,
} from '@pharmaplan/common';
import { useNavigate } from 'react-router-dom';
import { renderScreen } from '../../actions/drawerActions';
import { Constants, OtherScreens, ScreenTypes } from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';
import useDrawer from '../useDrawer';
import useWorkshiftDelete from '../Admin/useWorkshiftDelete';
import useDeleteAvailability from '../Admin/useDeleteAvailability';

const { ownershipBookings } = Constants.paths.admin;

export interface IOwnershipBookingsDates {
  startDate: string | null;
  endDate: string | null;
}

const useAdminReportsFn = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();
  const { showWorkshiftDelete, showRequestCancellation } = useWorkshiftDelete();
  const { showDeletePreSubmit } = useDeleteAvailability();

  const showExpressBooking = (id: string, workshift = false) => {
    openDrawer();

    const typeAndFromScreen = workshift
      ? {
        type: AdminLegendVariant.posted,
        fromScreen: OtherScreens.AdminPostedWorkshiftReports,
      }
      : {
        type: AdminLegendVariant.booked,
        fromScreen: OtherScreens.AdminBookingReports,
      };

    dispatch(
      renderScreen({
        ...typeAndFromScreen,
        screenNumber: 5,
        screenType: ScreenTypes.availableWorkshift,
        eventId: id,
      }),
    );
  };

  const findMatchingWorkshifts = (
    availabilityId: string,
    code: string,
    pharmacistId: string,
  ) => {
    openDrawer();

    dispatch(
      renderScreen({
        screenNumber: 5,
        screenType: ScreenTypes.pharmacistList,
        eventId: availabilityId,
        date: code,
        pharmacistId,
      }),
    );
  };

  const findMatchingAvailabilities = (workshiftId: string) => {
    openDrawer();
    dispatch(
      renderScreen({
        screenNumber: 4,
        screenType: ScreenTypes.availableWorkshift,
        eventId: workshiftId,
        type: PharmacistLegendVariant.workshift,
        fromScreen: OtherScreens.AdminPostedWorkshiftReports,
      }),
    );
  };

  const goToApproveBooking = ({
    bookingId,
    workshiftId,
    fromScreen,
    longTermShiftContractId,
    eventIds,
    professionalId,
  }: {
      bookingId: string;
      workshiftId: string;
      fromScreen: OtherScreens;
      longTermShiftContractId?: string;
      eventIds?: Array<string> | null;
      professionalId?: string;
    }) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 4,
          screenType: ScreenTypes.requestedList,
          eventId: bookingId,
          workshiftId,
          eventIds: eventIds ?? [],
          pharmacistId: professionalId,
          fromScreen,
          extraParameters: {
            longTermShiftContractId,
          },
        }),
      );
    };

  const showEditWorkshift = (workshiftId: string) => {
    openDrawer();
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 2,
        eventId: workshiftId,
        fromScreen: OtherScreens.AdminReports,
      }),
    );
  };

  const showAssignedAccounts = (adminUserId: string, adminName: string) => {
    openDrawer();
    dispatch(
      renderScreen({
        screenType: ScreenTypes.assignedAccounts,
        screenNumber: 1,
        eventId: adminUserId,
        extraParameters: { name: adminName },
      }),
    );
  };

  /**
   * The bookings are scoped to the range the report was last fetched with, not
   * to whatever is currently typed into the date filters, so the caller passes
   * the applied dates down rather than reading them from the form.
   */
  const goToOwnershipBookings = (dates: IOwnershipBookingsDates) =>
    (ownerId: string) =>
      () => {
        navigate(ownershipBookings, { state: { ownerId, ...dates } });
      };

  return {
    showAssignedAccounts,
    goToOwnershipBookings,
    showExpressBooking,
    findMatchingWorkshifts,
    findMatchingAvailabilities,
    showWorkshiftDelete,
    goToApproveBooking,
    showRequestCancellation,
    showDeletePreSubmit,
    showEditWorkshift,
  };
};
export default useAdminReportsFn;
